import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, displayName } from "../lib/auth";

/**
 * <WelcomeBanner />
 * --------------------------------------------------------------
 * Premium logged-in welcome banner. Renders nothing for anonymous
 * users (graceful no-op). Shows:
 *   - "Welcome back, {{name}}"
 *   - Subtle subtitle
 *   - Quick CTA(s): Profile + Admin (if user.is_admin)
 *
 * Designed to live at the very top of the homepage, ABOVE the hero,
 * so it greets returning users immediately without disrupting the
 * marketing hero design.
 */
export default function WelcomeBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (!user) return null;

  const name = displayName(user);

  return (
    <section
      className="relative border-b border-[#C8A24A]/20 bg-gradient-to-r from-[#0B0D12] via-[#15110A] to-[#0B0D12]"
      data-testid="welcome-banner"
    >
      <div className="app-container py-4 md:py-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] text-black grid place-items-center text-sm font-bold shrink-0">
            {(name || "U")[0]}
          </div>
          <div className="min-w-0">
            <div className="font-display text-base md:text-lg leading-tight truncate" data-testid="welcome-banner-greeting">
              {t("home.welcome_back", "Welcome back, {{name}}", { name })}
            </div>
            <div className="text-xs text-white/55 truncate">
              {user.is_admin
                ? t("home.welcome_admin_sub", "You\u2019re signed in as an admin. Your console is one click away.")
                : t("home.welcome_member_sub", "Continue exploring your DONiTSH!T journey.")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {user.is_admin && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#C8A24A]/40 bg-[#C8A24A]/10 gold-text hover:bg-[#C8A24A]/20 transition-colors"
              data-testid="welcome-banner-admin"
            >
              <ShieldCheck className="w-3 h-3" />
              {t("home.welcome_cta_admin", "Admin Console")}
            </Link>
          )}
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
            data-testid="welcome-banner-profile"
          >
            <Sparkles className="w-3 h-3" />
            {t("home.welcome_cta_profile", "My Profile")}
            <ArrowRight className="w-3 h-3 opacity-60" />
          </Link>
        </div>
      </div>
    </section>
  );
}
